import { useRef } from 'react';
import { useEffectOnce } from './useEffectOnce';

export const defaultBreakpoints = {
  phoneSmall: 320,
  phone: 375,
  tablet: 768,
  desktop: 960,
  widescreen: 1152,
  highdef: 1344,
  ultrahd: 1920
};

export function checkBreakpoint (breakpoint: string, breakpoints: { [key: string]: number } = defaultBreakpoints) {
  const parts = breakpoint.split(/([<>=]+)/g);
  parts.shift();
  
  const operator = parts.shift();

  const breakpointPx = breakpoints[ parts.shift() ];
  if (!breakpoint) return;

  switch (operator) {
  case '>':
    return window.innerWidth > breakpointPx;
  case '>=':
    return window.innerWidth >= breakpointPx;
  case '<':
    return window.innerWidth < breakpointPx;
  case '<=':
    return window.innerWidth <= breakpointPx;
  }
}

export function useResizeX (
  callback: (width: number, height: number, breakpoint: (breakpoint: string) => boolean) => void, 
  dontInit?: boolean, 
  breakpoints = defaultBreakpoints
) {
  useResize(callback, dontInit, breakpoints, 'height');
}

// Note that mobile devices often show or hide the address bar when scrolling
// which will invoke the resize handler. Set the 'ignoreDim' parameter to
// 'height' to supress this behaviour or use the useResizeX hook instead.
export function useResize (
  callback: (width: number, height: number, breakpoint: (breakpoint: string) => boolean) => void, 
  dontInit?: boolean, 
  breakpoints = defaultBreakpoints,
  ignoreDim:'width'|'height'|undefined = undefined
) {
  const lastWidthRef = useRef(-1);
  const lastHeightRef = useRef(-1);

  const cb = () => {
    const lastWidth = lastWidthRef.current;
    const curWidth = window.innerWidth;
    lastWidthRef.current = curWidth;

    const lastHeight = lastHeightRef.current;
    const curHeight = window.innerHeight;
    lastHeightRef.current = curHeight;
    
    if (lastWidth === curWidth && lastHeight === curHeight) return;

    if (ignoreDim === 'height' && lastWidth === curWidth) return;
    if (ignoreDim === 'width' && lastHeight === curHeight) return;

    callback(
      window.innerWidth, 
      window.innerHeight,
      (breakpoint) => checkBreakpoint(breakpoint, breakpoints)
    );
  };

  let handler: () => void, debounce: NodeJS.Timeout;
  useEffectOnce(() => {
    if (!dontInit) {
      cb();
    }
    
    handler = () => {
      clearTimeout(debounce);
      debounce = setTimeout(() => {
        cb();
      }, 400);
    };
    window.addEventListener('resize', handler);

    return () => {
      window.removeEventListener('resize', handler);
    };
  }, null, []);
}
